import React, {createContext, useContext, useReducer, useCallback, ReactNode} from 'react';
import produce from 'immer';
import {AppError, ErrorCategory} from '../types/errors';

/**
 * Error state managed by the context
 */
interface ErrorState {
    errors: AppError[];
    dismissedErrorIds: Set<string>;
    currentError: AppError | null;
    lastError: AppError | null;
    isOffline: boolean;
    consecutiveFailures: number;
}

/**
 * Actions for the error reducer
 */
type ErrorAction =
    | {type: 'ADD_ERROR'; payload: AppError}
    | {type: 'DISMISS_ERROR'; payload: string}
    | {type: 'CLEAR_ERRORS'}
    | {type: 'CLEAR_CATEGORY'; payload: ErrorCategory}
    | {type: 'SET_ERROR'; payload: AppError}
    | {type: 'CLEAR_ERROR'}
    | {type: 'NETWORK_FAILURE'}
    | {type: 'NETWORK_SUCCESS'};

/**
 * Context value with state and actions
 */
interface ErrorContextValue {
    errors: AppError[];
    addError: (error: AppError) => void;
    dismissError: (errorId: string) => void;
    clearErrors: () => void;
    clearErrorsByCategory: (category: ErrorCategory) => void;
    hasErrors: boolean;
    hasErrorsInCategory: (category: ErrorCategory) => boolean;
    // Additional methods for offline detection and single error tracking
    currentError: AppError | null;
    isOffline: boolean;
    consecutiveFailures: number;
    setError: (error: AppError) => void;
    clearError: () => void;
    retryLastError: () => void;
    dispatch: React.Dispatch<ErrorAction>;
}

const ErrorContext = createContext<ErrorContextValue | undefined>(undefined);

/**
 * Generates a unique error ID from error properties and timestamp
 */
function generateErrorId(error: AppError): string {
    return `${error.category}-${error.source}-${error.timestamp}`;
}

/**
 * Immer-based reducer for error state management
 */
const errorReducer = produce((draft: ErrorState, action: ErrorAction) => {
    switch (action.type) {
        case 'ADD_ERROR': {
            const errorId = generateErrorId(action.payload);
            // Don't add if already dismissed
            if (draft.dismissedErrorIds.has(errorId)) {
                return;
            }
            // Don't add duplicates (same category/source/timestamp)
            const isDuplicate = draft.errors.some(
                (e) => generateErrorId(e) === errorId
            );
            if (!isDuplicate) {
                draft.errors.push(action.payload);
            }
            break;
        }
        case 'DISMISS_ERROR': {
            draft.dismissedErrorIds.add(action.payload);
            draft.errors = draft.errors.filter(
                (e) => generateErrorId(e) !== action.payload
            );
            break;
        }
        case 'CLEAR_ERRORS': {
            draft.errors = [];
            draft.dismissedErrorIds.clear();
            break;
        }
        case 'CLEAR_CATEGORY': {
            draft.errors = draft.errors.filter(
                (e) => e.category !== action.payload
            );
            break;
        }
        case 'SET_ERROR': {
            draft.currentError = action.payload;
            draft.lastError = action.payload;
            break;
        }
        case 'CLEAR_ERROR': {
            draft.currentError = null;
            break;
        }
        case 'NETWORK_FAILURE': {
            draft.consecutiveFailures += 1;
            if (draft.consecutiveFailures >= 3) {
                draft.isOffline = true;
            }
            break;
        }
        case 'NETWORK_SUCCESS': {
            draft.consecutiveFailures = 0;
            draft.isOffline = false;
            break;
        }
    }
});

/**
 * Error Context Provider component
 * 
 * Manages global error state with deduplication and category-based filtering.
 * Uses Immer for immutable state updates.
 * 
 * @example
 * ```tsx
 * <ErrorProvider>
 *   <App />
 * </ErrorProvider>
 * ```
 */
export function ErrorProvider({children}: {children: ReactNode}) {
    const [state, dispatch] = useReducer(errorReducer, {
        errors: [],
        dismissedErrorIds: new Set<string>(),
        currentError: null,
        lastError: null,
        isOffline: false,
        consecutiveFailures: 0,
    });

    const addError = useCallback((error: AppError) => {
        dispatch({type: 'ADD_ERROR', payload: error});
    }, []);

    const dismissError = useCallback((errorId: string) => {
        dispatch({type: 'DISMISS_ERROR', payload: errorId});
    }, []);

    const clearErrors = useCallback(() => {
        dispatch({type: 'CLEAR_ERRORS'});
    }, []);

    const clearErrorsByCategory = useCallback((category: ErrorCategory) => {
        dispatch({type: 'CLEAR_CATEGORY', payload: category});
    }, []);

    const hasErrorsInCategory = useCallback(
        (category: ErrorCategory) => {
            return state.errors.some((e) => e.category === category);
        },
        [state.errors]
    );

    // Additional methods for offline detection and single error tracking
    const setError = useCallback((error: AppError) => {
        dispatch({type: 'SET_ERROR', payload: error});
    }, []);

    const clearError = useCallback(() => {
        dispatch({type: 'CLEAR_ERROR'});
    }, []);

    const retryLastError = useCallback(() => {
        if (state.lastError && state.lastError.isRetryable) {
            // Clear current error state to allow retry
            dispatch({type: 'CLEAR_ERROR'});
            // Reset offline state on manual retry
            dispatch({type: 'NETWORK_SUCCESS'});
        }
    }, [state.lastError]);

    const value: ErrorContextValue = {
        errors: state.errors,
        addError,
        dismissError,
        clearErrors,
        clearErrorsByCategory,
        hasErrors: state.errors.length > 0,
        hasErrorsInCategory,
        currentError: state.currentError,
        isOffline: state.isOffline,
        consecutiveFailures: state.consecutiveFailures,
        setError,
        clearError,
        retryLastError,
        dispatch,
    };

    return (
        <ErrorContext.Provider value={value}>{children}</ErrorContext.Provider>
    );
}

/**
 * Hook to access error context
 * 
 * @throws {Error} If used outside ErrorProvider
 * 
 * @example
 * ```tsx
 * const {addError, errors, dismissError} = useErrorContext();
 * ```
 */
export function useErrorContext(): ErrorContextValue {
    const context = useContext(ErrorContext);
    if (context === undefined) {
        throw new Error('useErrorContext must be used within an ErrorProvider');
    }
    return context;
}
